<script setup lang="ts">
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { RouterLink } from 'vue-router'
import LegalDrawer from '@/components/LegalDrawer.vue'
import { buildStartlineTermsHtml } from '@/utils/startlineTermsContent'
import { buildStartlineOrganizerTermsHtml } from '@/utils/startlineOrganizerTermsContent'
import { useAppConfig } from '@/composables/useAppConfig'
import { usePlatformContact } from '@/composables/usePlatformContact'
import { FALLBACK_COMPANY } from '@/constants/company'
import { SOCIAL_LINKS } from '@/constants/social'

withDefaults(defineProps<{ variant?: 'full' | 'compact' }>(), { variant: 'full' })

const { t } = useI18n()
const year = new Date().getFullYear()
const { config } = useAppConfig()

const { infoEmail, supportEmail, privacyHtml } = usePlatformContact()
const termsHtml = computed(() =>
  buildStartlineTermsHtml(
    config.value?.email ?? { info: 'info@startline.be' },
    config.value?.company ?? FALLBACK_COMPANY,
  ),
)
const organizerTermsHtml = computed(() =>
  buildStartlineOrganizerTermsHtml(
    config.value?.email ?? { info: 'info@startline.be' },
    config.value?.company ?? FALLBACK_COMPANY,
  ),
)

const operatorLine = computed(() => {
  const company = config.value?.company ?? FALLBACK_COMPANY
  return t('footer.operator_line', {
    kbo: company.kbo_number,
    vat: company.vat_number,
  })
})


// Entries without a url are stubs and must not render dead links.
const socialLinks = SOCIAL_LINKS.flatMap((link) => (link.url === null ? [] : [{ key: link.key, url: link.url }]))

const columns = [
  {
    key: 'product',
    links: [
      { to: '/for-organizers', label: 'nav.for_organizers' },
      { to: '/for-participants', label: 'nav.for_participants' },
      { to: '/docs', label: 'nav.docs' },
    ],
  },
  {
    key: 'company',
    links: [
      { to: '/about', label: 'footer.about' },
      { to: '/contact', label: 'footer.contact' },
    ],
  },
  {
    key: 'account',
    links: [
      { to: '/organizer/login', label: 'nav.organizer_login' },
      { to: '/my/login', label: 'nav.participant_login' },
    ],
  },
] as const

const privacyOpen = ref(false)
const participantTermsOpen = ref(false)
const organizerTermsOpen = ref(false)

const privacyTriggerRef = ref<HTMLElement | null>(null)
const participantTermsTriggerRef = ref<HTMLElement | null>(null)
const organizerTermsTriggerRef = ref<HTMLElement | null>(null)
</script>

<template>
  <footer class="bg-slate-900 text-slate-400 border-t-2 border-emerald-500">
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" :class="variant === 'full' ? 'py-12' : 'py-8'">
      <!-- Full variant: brand + link columns -->
      <div v-if="variant === 'full'" class="grid grid-cols-2 md:grid-cols-4 gap-8 pb-10 mb-8 border-b border-slate-800">
        <div class="col-span-2 md:col-span-1">
          <span class="text-xl font-black text-white tracking-tight">Startline</span>
          <p class="mt-3 text-sm text-slate-400 leading-relaxed">{{ t('footer.tagline') }}</p>
          <ul class="mt-4 space-y-1 text-sm">
            <li>
              <a :href="`mailto:${infoEmail}`" class="hover:text-white transition-colors">{{ infoEmail }}</a>
            </li>
            <li>
              <a :href="`mailto:${supportEmail}`" class="hover:text-white transition-colors">{{ supportEmail }}</a>
            </li>
          </ul>
          <ul v-if="socialLinks.length > 0" class="mt-4 flex items-center gap-4 text-sm">
            <li v-for="link in socialLinks" :key="link.key">
              <a :href="link.url" target="_blank" rel="noopener" class="hover:text-white transition-colors">
                {{ t(`footer.social.${link.key}`) }}
              </a>
            </li>
          </ul>
        </div>

        <nav v-for="column in columns" :key="column.key" :aria-label="t(`footer.columns.${column.key}`)">
          <p class="text-xs font-semibold uppercase tracking-widest text-slate-500">{{ t(`footer.columns.${column.key}`) }}</p>
          <ul class="mt-4 space-y-2">
            <li v-for="link in column.links" :key="link.to">
              <RouterLink :to="link.to" class="text-sm text-slate-400 hover:text-white transition-colors">
                {{ t(link.label) }}
              </RouterLink>
            </li>
          </ul>
        </nav>
      </div>

      <!-- Bottom bar (both variants) -->
      <div class="flex flex-col sm:flex-row items-center justify-between gap-4">
        <div class="text-sm">
          <p>{{ t('footer.copyright', { year }) }}</p>
          <p class="mt-1 text-xs text-slate-500">{{ operatorLine }}</p>
        </div>
        <div class="flex items-center gap-6 text-sm">
          <button
            ref="privacyTriggerRef"
            type="button"
            class="hover:text-slate-200 transition-colors"
            @click="privacyOpen = true"
          >
            {{ t('footer.privacy_policy') }}
          </button>
          <button
            ref="participantTermsTriggerRef"
            type="button"
            class="hover:text-slate-200 transition-colors"
            @click="participantTermsOpen = true"
          >
            {{ t('footer.terms_participants') }}
          </button>
          <button
            ref="organizerTermsTriggerRef"
            type="button"
            class="hover:text-slate-200 transition-colors"
            @click="organizerTermsOpen = true"
          >
            {{ t('footer.terms_organizers') }}
          </button>
        </div>
      </div>
    </div>

    <LegalDrawer
      :open="privacyOpen"
      initial-document="privacy_policy"
      :race-rules="null"
      :terms-and-conditions="null"
      :startline-terms="null"
      :privacy-policy="privacyHtml"
      :return-focus-to="privacyTriggerRef"
      @update:open="privacyOpen = $event"
    />

    <LegalDrawer
      :open="participantTermsOpen"
      initial-document="startline_terms"
      :race-rules="null"
      :terms-and-conditions="null"
      :startline-terms="termsHtml"
      :privacy-policy="null"
      :return-focus-to="participantTermsTriggerRef"
      @update:open="participantTermsOpen = $event"
    />

    <LegalDrawer
      :open="organizerTermsOpen"
      initial-document="startline_terms"
      :race-rules="null"
      :terms-and-conditions="null"
      :startline-terms="organizerTermsHtml"
      :privacy-policy="null"
      :return-focus-to="organizerTermsTriggerRef"
      @update:open="organizerTermsOpen = $event"
    />
  </footer>
</template>
